import { Close } from "@mui/icons-material";
import {
  FormControl,
  IconButton,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { ChangeEvent, FormEvent, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLogPatientWeightMutation } from "src/api/services/weightTrackerApi";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { desktopSx, mobileSx } from "src/utils/styles/modalStyles";

interface Props {
  open: boolean;
  onClose: (o: boolean) => void;
  height: number;
  setDisplay: (o: boolean) => void;
}

const NewWeightModal = ({ onClose, open, height, setDisplay }: Props) => {
  const isMobile = useIsMobile();
  const [newWeight] = useLogPatientWeightMutation();
  const { t } = useTranslation();
  const component = "weight-tracker.new-weight";
  const [weight, setWeight] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const errLog = useSentryLogger("error");

  const currentDate = useMemo(() => {
    const date = new Date();

    return format(date, "EEE, MMM d");
  }, []);

  const handleWeight = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setWeight(value);
  }, []);

  const handleClose = useCallback(() => {
    setWeight("");
    onClose(false);
  }, [onClose]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      try {
        await newWeight({
          date: new Date().toISOString(),
          weight: Number(weight),
          height: height,
        });
        setDisplay(true);
      } catch (error) {
        errLog("Could not add new weight", error);
      } finally {
        setLoading(false);
        handleClose();
      }
    },
    [errLog, handleClose, height, newWeight, setDisplay, weight],
  );

  return (
    <Modal open={open} onClose={handleClose}>
      <Stack sx={isMobile ? mobileSx : desktopSx} width={"200px"}>
        <IconButton onClick={handleClose} sx={{ alignSelf: "flex-end" }}>
          <Close />
        </IconButton>
        <Stack
          component={"form"}
          direction={"row"}
          alignItems={isMobile ? "center" : "end"}
          justifyContent={"space-between"}
          onSubmit={handleSubmit}
        >
          <Stack gap={3}>
            <Stack gap={1}>
              <Typography fontWeight={700} variant="h5">
                {t(`${component}.text.1`, "Date")}
              </Typography>
              <Typography variant="h6" color="#6C7278">
                {currentDate}
              </Typography>
            </Stack>
            <Stack gap={2}>
              <Typography fontWeight={700} variant="h5">
                {t(`${component}.text.2`, "Weight")} (lbs)
              </Typography>
              <FormControl>
                <TextField
                  required
                  label="lbs"
                  value={weight}
                  onChange={handleWeight}
                  sx={{ width: "120px" }}
                  type="number"
                />
              </FormControl>
            </Stack>
          </Stack>
          <LoadingButton
            sx={{ p: 3, width: isMobile ? "0" : "30%" }}
            loading={loading}
            type="submit"
          >
            {t(`${component}.button`, "Save")}
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default NewWeightModal;
